import React from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldCheck, Upload, FileSpreadsheet, AlertTriangle, Trash2 } from "lucide-react";

const BAND_COLOR = {
  Excellent: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Good: "text-blue-700 bg-blue-50 border-blue-200",
  Moderate: "text-amber-700 bg-amber-50 border-amber-200",
  Elevated: "text-orange-700 bg-orange-50 border-orange-200",
  Critical: "text-red-700 bg-red-50 border-red-200",
  Unknown: "text-slate-700 bg-slate-100 border-slate-200",
};

const LEVEL_PALETTE = {
  Low: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  High: "bg-red-50 text-red-700 border-red-200",
  Unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

const RESPONSE_PALETTE = {
  Mitigated: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Escalate: "bg-amber-50 text-amber-700 border-amber-200",
  Unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function CyberPostureTab({ accountId, isCustomer = false }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      if (isCustomer) {
        const r = await api.get("/portal/me/csa");
        setData(r.data.available ? { latest: r.data.assessment, history: [] } : { latest: null, history: [] });
      } else {
        const r = await api.get(`/accounts/${accountId}/csa`);
        setData(r.data);
      }
    } finally {
      setLoading(false);
    }
  }, [accountId, isCustomer]);

  React.useEffect(() => { load(); }, [load]);

  const onUpload = async (file) => {
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/accounts/${accountId}/csa/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (csaId) => {
    if (!window.confirm("Delete this assessment?")) return;
    await api.delete(`/csa/${csaId}`);
    await load();
  };

  if (loading) return <div className="text-sm text-slate-500 font-mono">Loading cyber posture…</div>;

  const latest = data?.latest;

  return (
    <div className="space-y-6" data-testid="cyber-posture">
      {!isCustomer && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Upload className="h-4 w-4 text-blue-600" /> Upload Current State Assessment
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Upload the customer's completed CSA workbook (.xlsx). The risk score is recalculated automatically and shown to both staff and the customer in their portal.
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              data-testid="csa-file-input"
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="csa-upload-btn"
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {uploading ? "Parsing & scoring…" : "Choose CSA workbook"}
            </button>
            {error && <div className="mt-3 text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{error}</div>}
          </CardContent>
        </Card>
      )}

      {!latest ? (
        <Card className="border-slate-200 p-12 text-center">
          <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No assessment uploaded yet</div>
          <div className="text-sm text-slate-500 mt-1">
            {isCustomer
              ? "Once Xentra completes your Current State Assessment it will appear here."
              : "Upload a Current State Assessment workbook to compute the risk score."}
          </div>
        </Card>
      ) : (
        <>
          <PostureCard latest={latest} />
          <PillarBreakdown pillars={latest.pillars_summary} detail={latest.pillars_detail} />
        </>
      )}

      {!isCustomer && data?.history?.length > 1 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading">Assessment history</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Uploaded</th>
                  <th className="px-4 py-2">Filename</th>
                  <th className="px-4 py-2">By</th>
                  <th className="px-4 py-2 text-right">Score</th>
                  <th className="px-4 py-2">Band</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`csa-history-${h.id}`}>
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-600">{fmtDate(h.created_at)}</td>
                    <td className="px-4 py-2">{h.filename}</td>
                    <td className="px-4 py-2 text-slate-600">{h.uploaded_by}</td>
                    <td className="px-4 py-2 text-right font-mono">{h.overall_score ?? "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-[10px] uppercase font-mono rounded border ${BAND_COLOR[h.rating_band] || BAND_COLOR.Unknown}`}>{h.rating_band}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => onDelete(h.id)} className="text-red-600 hover:text-red-800" data-testid={`csa-delete-${h.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PostureCard({ latest }) {
  const score = latest.overall_score;
  const band = latest.rating_band;
  const cls = BAND_COLOR[band] || BAND_COLOR.Unknown;
  return (
    <Card className="border-slate-200" data-testid="csa-overall-score">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 flex flex-col items-center justify-center text-center">
            <ScoreRing score={score} />
            <div className={`mt-3 inline-flex items-center px-3 py-1 text-xs uppercase font-mono tracking-wider rounded border ${cls}`}>
              {band}
            </div>
            <div className="mt-2 text-[11px] text-slate-500 font-mono">
              {latest.risk_total_count} risks assessed · {fmtDate(latest.created_at)}
            </div>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 gap-4">
            <LevelTile label="Low" value={latest.risk_count_by_level?.Low ?? 0} cls="bg-emerald-50 text-emerald-700 border-emerald-100" />
            <LevelTile label="Medium" value={latest.risk_count_by_level?.Medium ?? 0} cls="bg-amber-50 text-amber-700 border-amber-200" />
            <LevelTile label="High" value={latest.risk_count_by_level?.High ?? 0} cls="bg-red-50 text-red-700 border-red-200" />
            <LevelTile label="Unknown" value={latest.risk_count_by_level?.Unknown ?? 0} cls="bg-slate-100 text-slate-600 border-slate-200" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreRing({ score }) {
  const value = score ?? 0;
  const r = 50;
  const c = 2 * Math.PI * r;
  const dash = c * (value / 100);
  const colour = value >= 80 ? "#059669" : value >= 65 ? "#2563eb" : value >= 50 ? "#d97706" : value >= 35 ? "#ea580c" : "#dc2626";
  return (
    <svg width="140" height="140" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} stroke="#e2e8f0" strokeWidth="10" fill="none" />
      <circle cx="60" cy="60" r={r} stroke={colour} strokeWidth="10" fill="none"
        strokeDasharray={`${dash} ${c}`} strokeLinecap="round" transform="rotate(-90 60 60)" />
      <text x="60" y="62" textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: "ui-sans-serif", fontSize: 24, fontWeight: 700, fill: "#0f172a" }}>
        {score ?? "—"}
      </text>
      <text x="60" y="80" textAnchor="middle"
        style={{ fontFamily: "ui-monospace", fontSize: 9, fill: "#64748b", letterSpacing: 1 }}>
        / 100
      </text>
    </svg>
  );
}

function LevelTile({ label, value, cls }) {
  return (
    <div className={`rounded-md border p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-mono opacity-80">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function PillarBreakdown({ pillars, detail }) {
  const [openPillar, setOpenPillar] = React.useState(null);
  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading">Posture by pillar</CardTitle>
        <div className="text-xs text-slate-500 mt-1">Following the NIST framework. Click a pillar to see the underlying risks.</div>
      </CardHeader>
      <CardContent className="p-0">
        {pillars.map((p) => {
          const isOpen = openPillar === p.pillar;
          const cls = (p.score ?? 0) >= 80 ? "bg-emerald-500"
            : (p.score ?? 0) >= 65 ? "bg-blue-500"
            : (p.score ?? 0) >= 50 ? "bg-amber-500"
            : (p.score ?? 0) >= 35 ? "bg-orange-500"
            : "bg-red-500";
          const risks = detail?.[p.pillar] || [];
          return (
            <div key={p.pillar} className="border-b border-slate-100 last:border-b-0" data-testid={`pillar-${p.pillar.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <button
                onClick={() => setOpenPillar(isOpen ? null : p.pillar)}
                className="w-full text-left p-4 hover:bg-blue-50/30 transition-colors flex items-center gap-4"
              >
                <div className="flex-shrink-0 w-40 font-heading text-sm font-bold text-slate-900">{p.pillar}</div>
                <div className="flex-1">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${cls}`} style={{ width: `${p.score ?? 0}%` }} />
                  </div>
                </div>
                <div className="w-32 text-right font-mono text-sm text-slate-800">
                  <span className="font-bold">{p.score ?? "—"}</span><span className="text-slate-400 text-[11px]"> / 100</span>
                  <div className="text-[10px] font-mono text-slate-500">{p.risk_count} risks</div>
                </div>
              </button>
              {isOpen && risks.length > 0 && (
                <div className="px-4 pb-4 bg-slate-50/40">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                        <th className="px-3 py-2">Risk</th>
                        <th className="px-3 py-2">Response</th>
                        <th className="px-3 py-2">Commentary</th>
                        <th className="px-3 py-2">Level</th>
                        <th className="px-3 py-2">P × I</th>
                        <th className="px-3 py-2 text-right">Rating</th>
                        <th className="px-3 py-2">Recommendation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {risks.map((r, idx) => (
                        <tr key={idx} className="border-b border-slate-100 last:border-b-0 align-top">
                          <td className="px-3 py-2 text-[13px] text-slate-800 max-w-[260px]">{r.name}</td>
                          <td className="px-3 py-2 text-[12px] text-slate-700">{r.survey_response || "—"}</td>
                          <td className="px-3 py-2 text-[12px] text-slate-600 max-w-[220px]">{r.commentary || "—"}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 text-[10px] uppercase font-mono rounded border ${LEVEL_PALETTE[r.risk_level] || LEVEL_PALETTE.Unknown}`}>
                              {r.risk_level}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[11px] font-mono text-slate-500">{r.probability} × {r.impact}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">{r.rating}</td>
                          <td className="px-3 py-2 text-[12px] text-slate-700 max-w-[260px]">{r.recommendation || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
